import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../firebaseInit';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import * as firestorePaths from '../utils/firestorePaths';

const DEBUG_BRANDING = process.env.NODE_ENV === 'development';

const debugLog = (message, ...args) => {
  if (DEBUG_BRANDING) {
    console.log(`🎨 [BRANDING] ${message}`, ...args);
  }
};

/**
 * Default branding configuration
 * Used when team has no custom branding or user is not authenticated
 */
// Available fonts for branding customization
export const BRANDING_FONTS = [
  { value: 'system-ui', label: 'System Default' },
  { value: '\'Inter\', sans-serif', label: 'Inter' },
  { value: '\'Poppins\', sans-serif', label: 'Poppins' },
  { value: '\'Roboto\', sans-serif', label: 'Roboto' },
  { value: '\'Open Sans\', sans-serif', label: 'Open Sans' },
  { value: '\'Montserrat\', sans-serif', label: 'Montserrat' },
  { value: '\'Playfair Display\', serif', label: 'Playfair Display' },
  { value: '\'Merriweather\', serif', label: 'Merriweather' },
  { value: '\'Oswald\', sans-serif', label: 'Oswald' },
  { value: '\'Raleway\', sans-serif', label: 'Raleway' },
];

export const DEFAULT_BRANDING = {
  companyName: 'TreeGenie',
  tagline: 'The Call Script Genie', // Tagline/catchphrase
  showTaglineInSidebar: true, // Show tagline below company name in sidebar
  showTaglineInBrowserTitle: true, // Show tagline in browser tab title
  logoUrl: null, // Uses default app icon
  teamSlug: null, // Custom URL slug (e.g., "acme-realty" for /team/acme-realty)
  // Font and color customization
  titleFont: 'system-ui',
  titleColor: '#1D4ED8', // Blue-700
  taglineFont: 'system-ui',
  taglineColor: '#6B7280', // Gray-500
};

const BrandingContext = createContext();

// Helper to get cached branding from localStorage
const getCachedBranding = (teamId) => {
  if (!teamId) return null;
  try {
    const cached = localStorage.getItem(`branding_${teamId}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      debugLog('Loaded cached branding for team:', teamId, parsed);
      return parsed;
    }
  } catch (e) {
    console.warn('Failed to load cached branding:', e);
  }
  return null;
};

// Helper to cache branding in localStorage
const setCachedBranding = (teamId, brandingData) => {
  if (!teamId) return;
  try {
    localStorage.setItem(`branding_${teamId}`, JSON.stringify(brandingData));
    debugLog('Cached branding for team:', teamId);
  } catch (e) {
    console.warn('Failed to cache branding:', e);
  }
};

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  // userTeam now auto-switches when admin views another team
  const { isAuthenticated, userTeam, claims } = useAuth();
  
  // Initialize from cache for instant load, or use defaults
  const [branding, setBranding] = useState(() => {
    const cached = getCachedBranding(userTeam);
    return cached || DEFAULT_BRANDING;
  });
  const [isLoadingBranding, setIsLoadingBranding] = useState(false);
  const [brandingError, setBrandingError] = useState(null);

  const appId = firestorePaths.getArtifactProjectId();

  /**
   * Load branding for the current team
   * Uses real-time listener to update when branding changes
   */
  const loadBranding = useCallback(async () => {
    if (!isAuthenticated || !userTeam) {
      debugLog('No team ID, using default branding');
      setBranding(DEFAULT_BRANDING);
      return;
    }

    try {
      setIsLoadingBranding(true);
      setBrandingError(null);

      const teamPath = firestorePaths.getTeamPath(appId, userTeam);
      const teamDoc = await getDoc(doc(db, teamPath));

      if (teamDoc.exists()) {
        const teamData = teamDoc.data();
        const teamBranding = teamData.branding || {};

        debugLog('Loaded team branding:', teamBranding);

        // Merge with defaults - only override if values exist
        const newBranding = {
          companyName: teamBranding.companyName || DEFAULT_BRANDING.companyName,
          tagline: teamBranding.tagline ?? '', // Allow empty string
          showTaglineInSidebar: teamBranding.showTaglineInSidebar ?? DEFAULT_BRANDING.showTaglineInSidebar,
          showTaglineInBrowserTitle: teamBranding.showTaglineInBrowserTitle ?? DEFAULT_BRANDING.showTaglineInBrowserTitle,
          logoUrl: teamBranding.logoUrl || DEFAULT_BRANDING.logoUrl,
          teamSlug: teamBranding.teamSlug || DEFAULT_BRANDING.teamSlug,
          teamSlugLastChanged: teamBranding.teamSlugLastChanged || null,
          // Font and color customization
          titleFont: teamBranding.titleFont || DEFAULT_BRANDING.titleFont,
          titleColor: teamBranding.titleColor || DEFAULT_BRANDING.titleColor,
          taglineFont: teamBranding.taglineFont || DEFAULT_BRANDING.taglineFont,
          taglineColor: teamBranding.taglineColor || DEFAULT_BRANDING.taglineColor,
        };
        setBranding(newBranding);
        setCachedBranding(userTeam, newBranding); // Cache for instant load next time
      } else {
        debugLog('Team document not found, using defaults');
        setBranding(DEFAULT_BRANDING);
      }
    } catch (error) {
      console.error('❌ Error loading branding:', error);
      setBrandingError(error);
      setBranding(DEFAULT_BRANDING);
    } finally {
      setIsLoadingBranding(false);
    }
  }, [isAuthenticated, userTeam, appId]);

  // Immediately load from cache when team changes (for instant UI)
  useEffect(() => {
    if (userTeam) {
      const cached = getCachedBranding(userTeam);
      if (cached) {
        debugLog('Using cached branding for instant load');
        setBranding(cached);
      }
    }
  }, [userTeam]);

  // Set up real-time listener for branding changes
  useEffect(() => {
    if (!isAuthenticated || !userTeam) {
      setBranding(DEFAULT_BRANDING);
      return;
    }

    debugLog('Setting up branding listener for team:', userTeam);

    const teamPath = firestorePaths.getTeamPath(appId, userTeam);
    const unsubscribe = onSnapshot(
      doc(db, teamPath),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const teamData = docSnapshot.data();
          const teamBranding = teamData.branding || {};

          debugLog('Branding updated:', teamBranding);

          const newBranding = {
            companyName: teamBranding.companyName || DEFAULT_BRANDING.companyName,
            tagline: teamBranding.tagline ?? '', // Allow empty string
            showTaglineInSidebar: teamBranding.showTaglineInSidebar ?? DEFAULT_BRANDING.showTaglineInSidebar,
            showTaglineInBrowserTitle: teamBranding.showTaglineInBrowserTitle ?? DEFAULT_BRANDING.showTaglineInBrowserTitle,
            logoUrl: teamBranding.logoUrl || DEFAULT_BRANDING.logoUrl,
            teamSlug: teamBranding.teamSlug || DEFAULT_BRANDING.teamSlug,
            teamSlugLastChanged: teamBranding.teamSlugLastChanged || null,
            // Font and color customization
            titleFont: teamBranding.titleFont || DEFAULT_BRANDING.titleFont,
            titleColor: teamBranding.titleColor || DEFAULT_BRANDING.titleColor,
            taglineFont: teamBranding.taglineFont || DEFAULT_BRANDING.taglineFont,
            taglineColor: teamBranding.taglineColor || DEFAULT_BRANDING.taglineColor,
          };
          console.log('🎨 Branding loaded - logoUrl:', newBranding.logoUrl);
          setBranding(newBranding);
          setCachedBranding(userTeam, newBranding); // Cache for instant load next time
        }
      },
      (error) => {
        console.error('❌ Branding listener error:', error);
        setBrandingError(error);
      }
    );

    return () => {
      debugLog('Cleaning up branding listener');
      unsubscribe();
    };
  }, [isAuthenticated, userTeam, appId]);

  /**
   * Check if user can edit branding
   * Only team leads and admins can modify branding
   */
  const canEditBranding = useCallback(() => {
    return claims?.role === 'admin' || claims?.role === 'team_lead';
  }, [claims?.role]);

  const value = {
    branding,
    isLoadingBranding,
    brandingError,
    canEditBranding,
    refreshBranding: loadBranding,
    // Expose defaults for reset functionality
    DEFAULT_BRANDING,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;

